'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLogger } from 'next-axiom';
import { WISHLIST_QUERY_KEYS } from '../wishlist-constants';
import { WishlistProxyClient } from '../wishlist-proxy-client';
import type { WishlistError } from '../wishlist-types';
import { useWishlistSessionToken } from './use-wishlist-session-token';

function formatWishlistError(error: WishlistError): string {
  return typeof error === 'object' && error.message
    ? error.message
    : String(error);
}

export function useWishlistItems() {
  const { data: authToken } = useWishlistSessionToken();
  const log = useLogger().with({ location: 'useWishlistItems' });
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [WISHLIST_QUERY_KEYS.ALL, 'items'],
    queryFn: async () => {
      if (!authToken) {
        throw new Error('No wishlist session token available');
      }

      const client = new WishlistProxyClient(authToken);
      const response = await client.getItems();

      if (response.error) {
        log.error('Failed to fetch items', { error: response.error });

        // If it's an auth error, invalidate the session token to trigger refresh
        if (
          response.error.code === 'AUTH_ERROR' ||
          response.error.code === 'NETWORK_ERROR'
        ) {
          // Auth/Network error detected, invalidating session token
          await queryClient.invalidateQueries({
            queryKey: [WISHLIST_QUERY_KEYS.SESSION_TOKEN],
          });
        }

        throw new Error(formatWishlistError(response.error));
      }
      return response.data || [];
    },
    enabled: !!authToken,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
